/**
 * The footer added to the end of an officer card's WhatsApp message.
 *
 * Belongs to the SESSION, not the browser: everyone working the same session ID shares
 * and may edit it (it rides along with the roster state, so an edit shows up on the
 * other tabs). null means nobody has changed it, so the default applies; "" is a
 * deliberate "no footer". Same shape as utils/handoffOverlap.
 */
import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

export const DEFAULT_WHATSAPP_FOOTER =
  'Brought to you by your Roster Assistant. Please call 3100 (ACar) or 3200 (DCar) for more assistance. Take care and have a wonderful shift!';

/** null = the session hasn't set one. */
let current: string | null = null;
let loaded = false;
let inFlight: Promise<void> | null = null;
const listeners = new Set<(value: string | null) => void>();

function broadcast(): void {
  listeners.forEach((listener) => listener(current));
}

function effectiveWhatsAppFooter(value: string | null): string {
  return value ?? DEFAULT_WHATSAPP_FOOTER;
}

/** Adopt the value that came back with a roster-state payload. */
export function adoptWhatsAppFooter(value: string | null | undefined): void {
  if (value === undefined) return;
  loaded = true;
  if (value === current) return;
  current = value;
  broadcast();
}

/** Forget what we know — call when the session changes. */
export function resetWhatsAppFooter(): void {
  loaded = false;
  inFlight = null;
  if (current !== null) {
    current = null;
    broadcast();
  }
}

async function load(): Promise<void> {
  if (loaded || inFlight) return inFlight ?? undefined;
  inFlight = apiClient
    .getWhatsAppFooter()
    .then(({ whatsapp_footer }) => {
      loaded = true;
      if (whatsapp_footer !== current) {
        current = whatsapp_footer;
        broadcast();
      }
    })
    .catch(() => { /* offline — the default stands */ })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/** The session's footer text (default applied), and a setter that writes through to the
 *  session and rolls back if the write fails. Resolves true when saved. */
export function useWhatsAppFooter(): [string, (text: string | null) => Promise<boolean>] {
  const [value, setValue] = useState(current);

  useEffect(() => {
    listeners.add(setValue);
    setValue(current);
    void load();
    return () => { listeners.delete(setValue); };
  }, []);

  const save = async (text: string | null): Promise<boolean> => {
    const previous = current;
    current = text;
    loaded = true;
    broadcast();
    try {
      await apiClient.setWhatsAppFooter(text ?? DEFAULT_WHATSAPP_FOOTER);
      return true;
    } catch {
      current = previous;
      broadcast();
      return false;
    }
  };

  return [effectiveWhatsAppFooter(value), save];
}
